/* eslint-disable max-len */
/* eslint-disable react/jsx-props-no-spreading */
import { forwardRef, useRef, useState } from 'react';

// utils
import { cn } from '@repo/zolintra-utils';
// components
import { Icon } from '../Icon/Icon';

import styles from './TextField.module.css';

/**
 * `TextField` component for entering text, with optional support for icons and validation.
 *
 * @typedef {Object} Props The properties for the `TextField` component.
 * @property {string} props.name The name of the text field, used for identification.
 * @property {string} [props.label] The label associated with the text field.
 * @property {string} [props.type] The type of the input (e.g., 'text', 'password', 'date').
 * @property {string} [props.placeholder] The placeholder for the text field.
 * @property {string} [props.error] The error message to display below the field if an error exists.
 * @property {boolean} [props.disabled=false] If `true`, the field will be disabled.
 * @property {import('../Icon/Icon').IconType} [props.icon] The name of the icon.
 * @property {number} [props.maxWidth] The maximum width of the text field, used for styling.
 * @property {string} [props.className] Additional CSS classes to apply to the component.
 * @property {string|number} [props.min] The minimum value for the input.
 * @property {string|number} [props.max] The maximum value for the input.
 * @property {import("react").ChangeEventHandler<HTMLInputElement>} [props.onChange] The callback when a value changes.
 * @property {string} [props.defaultValue] The default value when the component mount.
 * @property {string} [props.value=''] The input value.
 * @property {import('react').ComponentPropsWithoutRef<"input">} [props.props]
 */

/**
 * @type React.ForwardRefExoticComponent<Props>
 */
export const TextField = forwardRef(({
  name,
  label = null,
  type = 'text',
  placeholder = null,
  error = null,
  disabled = false,
  icon = null,
  maxWidth = null,
  className = null,
  ...props
}, ref) => {
  const inputRef = useRef(null);

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  return (
    <div
      className={cn([styles.field, className])}
      style={maxWidth ? { maxWidth } : {}}
    >
      {label ? (
        <label htmlFor={name} className={styles.label}>{label}</label>
      ) : null}

      <div
        className={cn([
          styles.inputWrapper,
          error ? styles.hasError : '',
          disabled ? styles.disabled : '',
        ])}
      >
        <input
          ref={(element) => {
            inputRef.current = element;
            if (typeof ref === 'function') ref(element);
          }}
          id={name}
          name={name}
          type={isPasswordVisible ? 'text' : type}
          className={styles.input}
          placeholder={placeholder || label || null}
          disabled={disabled}
          {...props}
        />

        {type === 'password' ? (
          <button
            type="button"
            onClick={() => setIsPasswordVisible(!isPasswordVisible)}
            aria-label="password"
            className={cn([styles.icon, !disabled && styles.iconWithAction])}
          >
            <Icon icon={isPasswordVisible ? 'eyeSlash' : 'eye'} />
          </button>
        ) : null}

        {type === 'date' ? (
          <button
            type="button"
            onClick={() => inputRef?.current?.showPicker()}
            aria-label="date"
            className={cn([styles.icon, !disabled && styles.iconWithAction])}
            disabled={disabled}
          >
            <Icon icon="calendar" />
          </button>
        ) : null}

        {type !== 'password' && type !== 'date' && icon ? (
          <span className={styles.icon}>
            <Icon icon={icon} />
          </span>
        ) : null}
      </div>

      {error ? <span id={name ? `error-${name}` : ''} className={styles.error}>{error}</span> : null}
    </div>
  );
});

export default TextField;
